import {
    AccountSubtype,
    AccountType,
    AccountClass,
    BalanceSheetAccountClass,
} from "@common/constants/domains/accounting/accounts";
import {
    getAvailableAccountTypes,
    tryGetDefaultSubtypeForType,
} from "@common/constants/domains/accounting/accountTypeHelpers";
import { fullDateLongFormat } from "@common/helpers/date";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { AccountSearchCell, CellType } from "@components/spreadsheet/cells";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef } from "@components/spreadsheet/types";
import { useAccounts } from "@hooks/useAccounts";
import { useOverlays } from "contexts/OverlayContext";
import { format, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useRef } from "react";
import styles from "./BalanceSheetImporter.module.scss";
import { BalanceSheetImporterAction } from "./BalanceSheetImporterAction";
import { AccountProxyObject, BalanceSheetImportStep } from "./types";
import {
    AccountFieldErrors,
    ValidationErrors,
    validateAndTransformAccounts,
} from "./validation";

interface Props {
    availableClassifications?: BalanceSheetAccountClass[];
    accounts: AccountProxyObject[];
    onAccountsChange: (accounts: AccountProxyObject[]) => void;
    validationErrors: ValidationErrors;
    hasAttemptedSubmit: boolean;
    step: BalanceSheetImportStep;
    extractedDate: string | null;
    isMapping?: boolean;
}

export const BalanceSheetImporterSpreadsheet: React.FC<Props> = ({
    availableClassifications,
    accounts,
    onAccountsChange,
    validationErrors,
    hasAttemptedSubmit,
    step,
    extractedDate,
    isMapping,
}) => {
    const prevDataRef = useRef(accounts);
    const { selectedEntity } = useAccountingTabContext();
    const { accounts: existingAccounts } = useAccounts({
        entityId: selectedEntity?.id,
    });
    const { openOverlay } = useOverlays();

    const balanceLabel = useMemo(() => {
        if (!extractedDate) {
            return "Balance as of __________";
        }
        return `Balance as of ${format(parseISO(extractedDate), fullDateLongFormat)}`;
    }, [extractedDate]);

    const accountsRef = useRef(accounts);
    accountsRef.current = accounts;

    const handleCreateAccount = useCallback(
        (rowIdx: number) => {
            const account = accountsRef.current[rowIdx];
            if (!account.name?.trim()) {
                return;
            }

            if (!selectedEntity?.legalType) {
                return;
            }

            // Get account type - use row value or get default from classification
            let accountType = account.accountType as AccountType | null;
            const accountSubtype =
                account.accountSubtype as AccountSubtype | null;

            // If type is not set but classification is, get the first available type for that class
            if (!accountType && account.accountClass) {
                const availableTypes = getAvailableAccountTypes([
                    account.accountClass,
                ]);
                accountType = availableTypes[0] ?? null;
            }

            // If we still don't have a type, use a default
            if (!accountType) {
                accountType = AccountType.cash;
            }

            // If subtype is not set, get the first available subtype for the type
            const defaultSubtype =
                tryGetDefaultSubtypeForType(
                    selectedEntity.legalType,
                    accountType,
                ) ?? AccountSubtype.cashOnHand;

            // Use the row's classification if available, otherwise use all available
            const modalClassifications = account.accountClass
                ? [account.accountClass]
                : (availableClassifications as unknown as
                      | AccountClass[]
                      | undefined);

            openOverlay<"createCoaAccount">("createCoaAccount", {
                entityIds: [selectedEntity.id],
                accountName: account.name?.trim() ?? "",
                accountType,
                accountSubtype: accountSubtype ?? defaultSubtype,
                availableClassifications: modalClassifications,
                onAccountsCreated: (createdAccounts) => {
                    const matchingAccount = createdAccounts.find(
                        (a) =>
                            "entityId" in a && a.entityId === selectedEntity.id,
                    );

                    if (matchingAccount) {
                        const currentAccounts = accountsRef.current;
                        const updatedAccounts = [...currentAccounts];
                        updatedAccounts[rowIdx] = {
                            ...currentAccounts[rowIdx],
                            mappedKickAccountId: matchingAccount.id,
                            accountType: matchingAccount.type,
                            accountSubtype: matchingAccount.subtype,
                            isActionAccepted: true,
                        };
                        onAccountsChange(updatedAccounts);
                    }
                },
            });
        },
        [
            selectedEntity,
            availableClassifications,
            openOverlay,
            onAccountsChange,
        ],
    );

    const handleUpdateRow = useCallback(
        (rowIdx: number, updates: Partial<AccountProxyObject>) => {
            const currentAccounts = accountsRef.current;
            const updatedAccounts = [...currentAccounts];
            updatedAccounts[rowIdx] = {
                ...currentAccounts[rowIdx],
                ...updates,
            };
            onAccountsChange(updatedAccounts);
        },
        [onAccountsChange],
    );

    const columns: ColumnDef[] = useMemo(() => {
        if (step === BalanceSheetImportStep.EnterBalances) {
            return [
                {
                    field: "name",
                    label: "Account",
                    cellType: CellType.STRING,
                    required: true,
                    isValid: ({ row }) =>
                        !hasAttemptedSubmit ||
                        !(
                            validationErrors.accounts?.[row] as
                                | AccountFieldErrors
                                | undefined
                        )?.name,
                },
                {
                    field: "accountClass",
                    label: "Classification",
                    cellType: CellType.ACCOUNT_CLASS,
                    balanceSheetClassesOnly: true,
                    required: true,
                    isValid: ({ row }) =>
                        !hasAttemptedSubmit ||
                        !(
                            validationErrors.accounts?.[row] as
                                | AccountFieldErrors
                                | undefined
                        )?.accountClass,
                },
                {
                    field: "balance",
                    label: balanceLabel,
                    cellType: CellType.NUMBER,
                    isMonetary: true,
                    required: true,
                    isValid: ({ row }) =>
                        !hasAttemptedSubmit ||
                        !(
                            validationErrors.accounts?.[row] as
                                | AccountFieldErrors
                                | undefined
                        )?.balance,
                },
            ];
        }

        return [
            {
                field: "name",
                label: "Account",
                cellType: CellType.STRING,
                required: true,
                isEditable: false,
                isValid: ({ row }) =>
                    !hasAttemptedSubmit ||
                    !(
                        validationErrors.accounts?.[row] as
                            | AccountFieldErrors
                            | undefined
                    )?.name,
            },
            {
                field: "accountClass",
                label: "Classification",
                cellType: CellType.ACCOUNT_CLASS,
                balanceSheetClassesOnly: true,
                required: false,
                isEditable: false,
                isValid: () => true,
            },
            {
                field: "balance",
                label: balanceLabel,
                cellType: CellType.NUMBER,
                isMonetary: true,
                required: false,
                isEditable: false,
                isValid: () => true,
            },
            {
                field: "mappedKickAccountId",
                label: "Mapped Account",
                cellFn: (props) => {
                    const accountRow = props.row as AccountProxyObject;
                    const isNewlyCreated =
                        accountRow.isActionAccepted &&
                        accountRow.mappedKickAccountId;
                    return (
                        <div
                            className={
                                isNewlyCreated
                                    ? styles.newlyCreatedAccount
                                    : undefined
                            }
                        >
                            <AccountSearchCell
                                {...props}
                                value={props.value as string | null}
                                accounts={existingAccounts}
                            />
                        </div>
                    );
                },
                required: false,
                isValid: ({ row }) =>
                    !hasAttemptedSubmit ||
                    !(
                        validationErrors.accounts?.[row] as
                            | AccountFieldErrors
                            | undefined
                    )?.mappedKickAccountId,
            },
            {
                field: "actions",
                label: "Actions",
                cellFn: (props) => (
                    <div className={styles.actionsCell}>
                        <BalanceSheetImporterAction
                            row={props.row as AccountProxyObject}
                            rowIdx={props.rowIdx}
                            onUpdateRow={handleUpdateRow}
                            onCreateAccount={handleCreateAccount}
                            isMapping={isMapping}
                        />
                    </div>
                ),
            },
        ];
    }, [
        step,
        balanceLabel,
        existingAccounts,
        validationErrors,
        hasAttemptedSubmit,
        handleCreateAccount,
        handleUpdateRow,
        isMapping,
    ]);

    const handleChange = (newData: Array<Record<string, any>>) => {
        onAccountsChange(newData as AccountProxyObject[]);
    };

    useEffect(() => {
        const validatedData = validateAndTransformAccounts({
            newData: accounts,
            prevData: prevDataRef.current,
            existingAccounts,
        });

        const hasChanges = validatedData.some(
            (row, idx) =>
                row.mappedKickAccountId !== accounts[idx]?.mappedKickAccountId,
        );

        if (hasChanges) {
            prevDataRef.current = validatedData;
            onAccountsChange(validatedData);
        } else {
            prevDataRef.current = accounts;
        }
    }, [accounts, existingAccounts, onAccountsChange]);

    const isEnterBalancesStep = step === BalanceSheetImportStep.EnterBalances;

    const gridTemplateColumns = isEnterBalancesStep
        ? "1fr 140px 180px"
        : "1fr 140px 180px 450px 200px";

    return (
        <SpreadsheetGrid
            stickyHeader
            noOuterBorder
            minSpareRows={20}
            emptySpareRows={!isEnterBalancesStep}
            data={accounts}
            columns={columns}
            onChange={handleChange}
            gridTemplateColumns={gridTemplateColumns}
            rowClassName={styles.spreadsheetRow}
            className={styles.spreadsheet}
            rowHoverHighlight
            lightNotEditableCells
            showDropdownIndicator
            overflow="auto"
        />
    );
};
